import React from "react"
import autobind from "class-autobind"
import { Table, TableBody, TableHeader, TableHeaderColumn, TableRow, TableRowColumn } from "material-ui/Table"
import strings from "../../../localization/strings"
import AccountStore from "../../../stores/AccountStore"
import PayrollTimeByHierarchyEmployeeDayPunchSetRow from "./PayrollTimeByHierarchyEmployeeDayPunchSetRow"
import PayrollTimeByHierarchyEmployeeDayTimeExpenseRow from "./PayrollTimeByHierarchyEmployeeDayTimeExpenseRow"
import PayrollTimeByHierarchyEmployeeDayAmountExpenseRow from "./PayrollTimeByHierarchyEmployeeDayAmountExpenseRow"
import ImageTimer from "material-ui/svg-icons/image/timer"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import * as permissionHelper from "../../../helpers/permissionHelper"
import { IPayrollTimeHierarchyEmployeeJobCostingSummary, ITimeTotals, EPunchSetApprovalType, IDateRange } from "../../../models"
import ApproveIcon from "../../../components/approve/ApproveIcon"
import * as timeHelper from "../../../helpers/timeHelper"
import { checkJobFieldAvailable } from "../../../helpers/jobHelper"
import { checkPositionFieldAvailable } from "../../../helpers/positionHelper"
import moment from "moment"
import { checkPhaseFieldAvailable } from "../../../helpers/punchHelper"

interface IProps {
	payrollDates: IDateRange
	daysData: IPayrollTimeHierarchyEmployeeJobCostingSummary[]
	timeTotals: ITimeTotals
	employeeId: string
	showTimecardPopup(employeeId: string, referenceDate: string): void
}
interface IState {
	expandRow: number
}

export default class PayrollTimeByHierarchyEmployeeDayRow extends React.Component<IProps, IState> {
	constructor(props) {
		super(props)
		this.state = {
			expandRow: -1,
		}
		autobind(this)
	}

	handleClickRow(columnId, clickedRow?) {
		if (columnId === 0 && permissionHelper.hasPermission(AuthenticationStore.Permissions.Timecards.ViewTimecards) && this.props.showTimecardPopup) {
			const referenceDate = this.props.daysData.length > 0 ? this.props.daysData[clickedRow].Date : this.props.payrollDates.StartDate
			this.props.showTimecardPopup(this.props.employeeId, timeHelper.keepConvertUTCToLocal(referenceDate))
		} else {
			this.setState({ expandRow: clickedRow === this.state.expandRow ? -1 : clickedRow })
		}
	}

	render() {
		var formatter = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
		});

		const { daysData, timeTotals } = this.props
		const { expandRow } = this.state
		return (
			<Table style={{ backgroundColor: "none" }} selectable={false}>
				<TableHeader displaySelectAll={false} adjustForCheckbox={false}>
					<TableRow>
						<TableHeaderColumn style={styles.iconCol} />
						<TableHeaderColumn className="TextOverflow" title={strings.Date} style={styles.dateCol}>{strings.Date}</TableHeaderColumn>
						<TableHeaderColumn className="TextOverflow" title={strings.Hierarchy} style={styles.nameCol}>{strings.Hierarchy}</TableHeaderColumn>
						{AccountStore.getShowPayrollRates() &&
							<TableHeaderColumn className="TextOverflow" title={`${strings.Pay} ${strings.Amount}`} style={styles.amountCol}>{strings.Pay} {strings.Amount}</TableHeaderColumn>
						}
						{AccountStore.getExpensesEnabled() && AccountStore.getExpenseIsPayrollTime() &&
							<TableHeaderColumn className="TextOverflow" title={strings.Amount} style={styles.amountCol}>{strings.Amount}</TableHeaderColumn>
						}
						{AccountStore.getJobsEnabled() && [
							<TableHeaderColumn className="TextOverflow" title={checkJobFieldAvailable('singular') ? AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel : strings.Job} style={styles.nameCol} key="job">{checkJobFieldAvailable('singular') ? AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel : strings.Job}</TableHeaderColumn>,
							<TableHeaderColumn className="TextOverflow" title={strings.Phase} style={styles.nameCol} key="phase">{checkPhaseFieldAvailable("singular") ? AccountStore.getCurrentAccountSettings().CustomFieldSettings.PhaseFieldLabels.SingularPhaseLabel :strings.Phase}</TableHeaderColumn>
						]}
						<TableHeaderColumn className="TextOverflow" title={checkPositionFieldAvailable('singular') ? AccountStore.getCurrentAccountSettings().CustomFieldSettings.PositionFieldLabels.SingularPositionLabel : strings.Position} style={styles.nameCol}>{checkPositionFieldAvailable('singular') ? AccountStore.getCurrentAccountSettings().CustomFieldSettings.PositionFieldLabels.SingularPositionLabel : strings.Position}</TableHeaderColumn>
						{AccountStore.getPayClassEnabled() &&
							<TableHeaderColumn className="TextOverflow" title={strings.Pay_Class} style={styles.nameCol}>{strings.Pay_Class}</TableHeaderColumn>
						}
						{AccountStore.getEquipmentEnabled() &&
							<TableHeaderColumn className="TextOverflow" title={strings.Equipment} style={styles.nameCol}>{strings.Equipment}</TableHeaderColumn>
						}
						{AccountStore.getEmployeeApproveTimecardEnabled() &&
							<TableHeaderColumn className="TextOverflow" title={strings.Employee} style={styles.approvedCol}>{strings.Employee}</TableHeaderColumn>
						}
						<TableHeaderColumn className="TextOverflow" title={strings.Supervisor} style={styles.approvedCol}>{strings.Supervisor}</TableHeaderColumn>
						{AccountStore.getExtSupApproveTimecardEnabled() &&
							<TableHeaderColumn className="TextOverflow" title={strings.External} style={styles.approvedCol}>{strings.External}</TableHeaderColumn>
						}
						<TableHeaderColumn className="TextOverflow" title={strings.Open_Punches} style={styles.missingPunchedCol}>{strings.Open_Punches}</TableHeaderColumn>
						<TableHeaderColumn className="TextOverflow" title={strings.TotalHours} style={styles.hourCol}>{strings.TotalHours}</TableHeaderColumn>
						<TableHeaderColumn className="TextOverflow" title={strings.Reg_Hours} style={styles.hourCol}>{strings.Reg_Hours}</TableHeaderColumn>
						<TableHeaderColumn className="TextOverflow" title={strings.OT_Hours} style={styles.hourCol}>{strings.OT_Hours}</TableHeaderColumn>
						{!AccountStore.getHideOT2() &&
							<TableHeaderColumn className="TextOverflow" title={strings.OT2_Hours} style={styles.hourCol}>{strings.OT2_Hours}</TableHeaderColumn>
						}
						{timeTotals.PayTypeTotals.map((payTypeTotal, index) =>
							<TableHeaderColumn key={index} className="TextOverflow" style={styles.hourCol} title={payTypeTotal.PayTypeName}>{payTypeTotal.PayTypeCode}</TableHeaderColumn>
						)}
					</TableRow>
				</TableHeader>
				<TableBody displayRowCheckbox={false}>
					{daysData.length === 0 ?
						<TableRow>
							<TableRowColumn style={{ paddingLeft: 0, paddingRight: 0 }}>
								<Table
									style={{ backgroundColor: "none" }}
									onCellClick={(rowNumber, columnId) => this.handleClickRow(columnId)}
								>
									<TableBody displayRowCheckbox={false}>
										<TableRow
											data-intercom-target={"menu-Payroll-PayrollTimeHierarchyTable-openEmployeeDayRowDetails"}
											style={{ cursor: "pointer", height: 48 }}
										>
											<TableRowColumn data-intercom-target={"menu-Payroll-PayrollTimeHierarchyTable-Btn-Add_Timecard"} style={styles.iconCol}>
												<ImageTimer hoverColor="#9E9E9E" />
											</TableRowColumn>
											<TableRowColumn style={styles.dateCol}>{strings.No_Time_Logged}</TableRowColumn>
											<TableRowColumn />
										</TableRow>
									</TableBody>
								</Table>
							</TableRowColumn>
						</TableRow> :
						daysData.map((dayData, index) => {
							const dayDataDate = timeHelper.keepConvertUTCToLocal(dayData.Date)
							return (
								<TableRow key={index}>
									<TableRowColumn style={{ paddingLeft: 0, paddingRight: 0 }}>
										<Table style={{ backgroundColor: "none" }} onCellClick={(rowNumber, columnId) => this.handleClickRow(columnId, index)}>
											<TableBody displayRowCheckbox={false}>
												<TableRow
													data-intercom-target={index === 0 && "menu-Payroll-PayrollTimeHierarchyTable-openEmployeeDayRowDetails"}
													style={{
														cursor: "pointer",
														height: 48,
														borderBottom: index === expandRow ? "solid 1px rgb(204, 204, 204)" : "none"
													}}
												>
													<TableRowColumn data-intercom-target={index === 0 && "menu-Payroll-PayrollTimeHierarchyTable-Btn-Add_Timecard"} style={styles.iconCol}>
														<ImageTimer hoverColor="#9E9E9E" />
													</TableRowColumn>
													<TableRowColumn style={styles.dateCol}>
														<table>
															<tr>
																<td>
																	<div style={styles.dateDate}>
																		{moment(dayDataDate).format("Do")}
																	</div>
																</td>
																<td>
																	<div style={styles.dateDayName}>
																		{moment(dayDataDate).format("ddd").toUpperCase()}
																	</div>
																	<div style={styles.dateDateLabel}>
																		{moment(dayDataDate).format("MMM YYYY")}
																	</div>
																</td>
															</tr>
														</table>
													</TableRowColumn>
													<TableRowColumn style={styles.nameCol} title={dayData.HierarchyName}>{dayData.HierarchyName}</TableRowColumn>
													{AccountStore.getShowPayrollRates() &&
														<TableRowColumn style={styles.amountCol}>{formatter.format(dayData.TimeTotals.TotalPay)}</TableRowColumn>
													}
													{AccountStore.getExpensesEnabled() && AccountStore.getExpenseIsPayrollTime() &&
														<TableRowColumn style={styles.amountCol}>{formatter.format(dayData.TimeTotals.TotalAmount)}</TableRowColumn>
													}
													{AccountStore.getJobsEnabled() && [
														<TableRowColumn style={styles.nameCol} key="job" title={dayData.JobName}>{dayData.JobName}</TableRowColumn>,
														<TableRowColumn style={styles.nameCol} key="phase" title={dayData.PhaseName}>{dayData.PhaseName}</TableRowColumn>
													]}
													<TableRowColumn style={styles.nameCol} title={dayData.PositionName}>{dayData.PositionName}</TableRowColumn>
													{AccountStore.getPayClassEnabled() &&
														<TableRowColumn style={styles.nameCol} title={dayData.PayClassName}>{dayData.PayClassName}</TableRowColumn>
													}
													{AccountStore.getEquipmentEnabled() &&
														<TableRowColumn style={styles.nameCol} title={dayData.EquipmentName}>{dayData.EquipmentName}</TableRowColumn>
													}
													{AccountStore.getEmployeeApproveTimecardEnabled() &&
														<TableRowColumn style={styles.approvedCol}>
															<ApproveIcon
																identify={`EmployeeDayRowEmpApprove${index}`}
																toolTipPosition="bottom"
																toolTipArrow="center"
																width={14}
																approvalType={EPunchSetApprovalType.Employee}
																punchSets={dayData.PayrollPunchSets}
																expenses={dayData.PayrollExpenses}
															/>
														</TableRowColumn>
													}
													<TableRowColumn style={styles.approvedCol}>
														<ApproveIcon
															identify={`EmployeeDayRowApprove${index}`}
															toolTipPosition="bottom"
															toolTipArrow="center"
															width={18}
															approvalType={EPunchSetApprovalType.Supervisor}
															punchSets={dayData.PayrollPunchSets}
															expenses={dayData.PayrollExpenses}
														/>
													</TableRowColumn>
													{AccountStore.getExtSupApproveTimecardEnabled() &&
														<TableRowColumn style={styles.approvedCol}>
															<ApproveIcon
																identify={`EmployeeDayRowExtSupApprove${index}`}
																toolTipPosition="bottom"
																toolTipArrow="center"
																width={14}
																approvalType={EPunchSetApprovalType.ExtSupervisor}
																punchSets={dayData.PayrollPunchSets}
																expenses={[]}
															/>
														</TableRowColumn>
													}
													<TableRowColumn style={styles.missingPunchedCol}>{dayData.PayrollPunchSets.filter(i => !i.PunchOutId).length}</TableRowColumn>													
													<TableRowColumn style={styles.hourCol}>{dayData.TimeTotals && dayData.TimeTotals.TotalProductiveHours}</TableRowColumn>
													<TableRowColumn style={styles.hourCol}>{dayData.TimeTotals && dayData.TimeTotals.RegularHours}</TableRowColumn>
													<TableRowColumn style={styles.hourCol}>{dayData.TimeTotals && dayData.TimeTotals.OvertimeHours}</TableRowColumn>
													{!AccountStore.getHideOT2() &&
														<TableRowColumn style={styles.hourCol}>{dayData.TimeTotals && dayData.TimeTotals.DoubleOvertimeHours}</TableRowColumn>
													}
													{timeTotals.PayTypeTotals.map((payTypeTotal, index) => {
														let payTypeTotalList = dayData.TimeTotals.PayTypeTotals.filter((ptt) => {
															return ptt.PayTypeId == payTypeTotal.PayTypeId;
														})
														let rowTotal = payTypeTotalList && payTypeTotalList.length > 0 ? payTypeTotalList[0].TotalHours : 0
														return (
															<TableRowColumn style={styles.hourCol} key={index}>{rowTotal}</TableRowColumn>
														)
													})}
												</TableRow>
												{index === expandRow &&
													<TableRow>
														<TableRowColumn
															colSpan={10 +
																(AccountStore.getHideOT2() ? 0 : 1) +
																(timeTotals.PayTypeTotals ? timeTotals.PayTypeTotals.length : 0) +
																(AccountStore.getShowPayrollRates() ? 1 : 0) +
																(AccountStore.getPayClassEnabled() ? 1 : 0) +
																(AccountStore.getExpensesEnabled() && AccountStore.getExpenseIsPayrollTime() ? 1 : 0)
															}
															style={{ paddingLeft: 90, paddingBottom: 30, paddingTop: 30 }}
														>
															<div onClick={(e) => e.stopPropagation()}>
																<PayrollTimeByHierarchyEmployeeDayPunchSetRow payrollPunchSets={dayData.PayrollPunchSets} />
																{AccountStore.getExpensesEnabled() && AccountStore.getExpenseIsPayrollTime() && [
																	<PayrollTimeByHierarchyEmployeeDayTimeExpenseRow
																		key="time"
																		payrollExpenses={dayData.PayrollExpenses}
																	/>,
																	<PayrollTimeByHierarchyEmployeeDayAmountExpenseRow
																		key="amount"
																		payrollExpenses={dayData.PayrollExpenses}
																	/>
																]}
															</div>
														</TableRowColumn>
													</TableRow>
												}
											</TableBody>
										</Table>
									</TableRowColumn>
								</TableRow>
							)
						})}
				</TableBody>
			</Table>
		)
	}
}

const styles = {
	iconCol: {
		width: 30,
		paddingLeft: 0,
		paddingRight: 0
	},
	dateCol: {
		paddingLeft: 0,
		paddingRight: 20,
		width: 110
	},
	dateDate: {
		fontSize: 25,
		marginRight: 5
	},
	dateDayName: {},
	dateDateLabel: {
		fontSize: 10,
		marginTop: -10
	},
	missingPunchedCol: {
		width: 80,
		paddingLeft: 10,
		paddingRight: 10
	},
	hourCol: {
		paddingLeft: 5,
		paddingRight: 5,
	},
	nameCol: {
		paddingLeft: 5,
		paddingRight: 5
	},
	approvedCol: {
		paddingLeft: 5,
		paddingRight: 5,
		width: 80,
		textAlign: "center"
	},
	amountCol: {
		paddingLeft: 5,
		paddingRight: 5,
		width: 80
	}
}